import { useCallback, useState } from 'react';
import { apiKeyManager } from '@empiriecom/mybuy-session/ApiKey';
import type { LoginResponsePayload } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LoginResponsePayload';
import type { LegacyCredentials } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/LegacyCredentials';
import type { CaptchaData } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import { CaptchaDataFromJSON } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/CaptchaData';
import { CredentialsTypeEnum } from '@empiriecom/mybuy-frontend-api/backend-mybuy-customer/v1/models/Credentials';
import { useSWRConfig } from 'swr';
import type { GTMEventGlycerinDisplayFormSuccess } from '@packages/tracking/src/types/events';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useLoginApi } from './LoginApi';
import { useLogout } from './useLogout';
import {
  postLoginActionsStorage,
  PostLoginActionType,
} from '../../PostLoginActions/postLoginActions';

export interface ShowCaptchaData {
  captchaData: CaptchaData | undefined;
  lastLogin: () => Promise<void>;
}

const noOp = async () => {};

export const useLogin = (onSuccess?: () => void) => {
  const { locale } = useI18n();
  const loginApi = useLoginApi();
  const { mutate } = useSWRConfig();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showCaptchaState, setShowCaptchaState] = useState<ShowCaptchaData>();
  const { logout } = useLogout();
  const dispatchGTMEvent = useTracking();

  const removeCaptchaData = useCallback(() => {
    setShowCaptchaState({ lastLogin: showCaptchaState?.lastLogin || noOp, captchaData: undefined });
  }, [showCaptchaState]);

  // wrap loginFn so it can be reused after captcha check if needed
  const executeLogin = useCallback(
    async (loginFn: () => Promise<LoginResponsePayload>) => {
      const attemptLogin = async (): Promise<void> => {
        removeCaptchaData();
        setLoading(true);
        setError(null);

        try {
          const result = await loginFn();
          if (result.errors && result.errors.elements?.length) {
            setError(result.errors.elements[0].description);
            return;
          }

          // Collect post-login actions from backend flags
          const postLoginActions: PostLoginActionType[] = [];

          if (result.warnings?.flags?.CheckBillingAddress) {
            postLoginActions.push(PostLoginActionType.CHECK_BILLING_ADDRESS);
          }

          if (result.warnings?.flags?.CheckEmailAddress) {
            postLoginActions.push(PostLoginActionType.CHECK_EMAIL);
          }

          if (result.warnings?.flags?.TACReconfirmationRequired) {
            postLoginActions.push(PostLoginActionType.TAC_RECONFIRMATION);
          }

          if (result.tokens) {
            // Set tokens immediately - no more tmpToken!
            await apiKeyManager.setApiKeys(result.tokens, result.trackingFlags);
            mutate('session');

            // Add post-login actions to localStorage if any exist
            if (postLoginActions.length > 0) {
              postLoginActionsStorage.setActions(postLoginActions);
            }

            dispatchGTMEvent<GTMEventGlycerinDisplayFormSuccess>({
              event: 'DisplayFormSuccess',
              DisplayFormSuccessData: {
                category: 'login-form',
              },
            });

            onSuccess && onSuccess();
          }
        } catch (response: any) {
          if (response && response.status === 503) {
            const captchaDataFromJSON = CaptchaDataFromJSON(await response.json());
            setShowCaptchaState({
              captchaData: captchaDataFromJSON,
              lastLogin: attemptLogin,
            });
          } else {
            // Response is thrown from generated api - I don't want to break this behaviour
            /* istanbul ignore next */ throw response;
          }
        } finally {
          // prevent flashing of dialog after loading is finished
          setTimeout(() => {
            setLoading(false);
          }, 250);
        }
      };

      return attemptLogin();
    },
    [removeCaptchaData, mutate, dispatchGTMEvent, onSuccess],
  );

  const login = ({
    email,
    password,
    remember,
  }: {
    email: string;
    password: string;
    remember?: boolean;
  }) => {
    if (!loginApi) {
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.error('[useLogin] loginApi not initialized when login was attempted');
      }
      setError('Login is temporarily unavailable. Please try again.');
      return;
    }

    executeLogin(() =>
      loginApi.login({
        ecLocale: locale,
        credentials: {
          type: CredentialsTypeEnum.Password,
          passwordCredentials: {
            email,
            password,
          },
          useAutologin: remember,
        },
      }),
    );
  };

  const legacyLogin = (legacyCredentials: LegacyCredentials, remember: boolean) => {
    if (loginApi) {
      executeLogin(() =>
        loginApi.login({
          ecLocale: locale,
          credentials: {
            type: CredentialsTypeEnum.Legacy,
            legacyCredentials,
            useAutologin: remember,
          },
        }),
      );
    }
  };

  return {
    loading,
    error,
    login,
    legacyLogin,
    logout,
    showCaptchaState,
    removeCaptchaData,
  };
};
